import { gql, DocumentNode } from '@apollo/client';
import {
	Box,
	Link,
	List,
	ListItem,
	ListItemText,
	Typography,
} from '@mui/material';
import React, { useContext } from 'react';
import styles from './InvoiceViewBillTo.module.scss';
import rpcShared from '@rockpapercoin/rpc-shared';
import { TeamMemberRole, InvoiceViewBillToInvoiceFragment } from '#types';
import classNames from 'classnames';
import { ContactsContext } from '../../../../context/ContactsContextProvider/ContactsContextProvider';

const invoiceViewBillToInvoiceFragment = gql`
	fragment invoiceViewBillToInvoice on Invoice {
		id
		contact {
			id
			firstName
			lastName
			companyName
			customer {
				id
				email
			}
			assignedMember {
				id
				user {
					id
				}
			}
			vendor {
				id
				users {
					id
					role
					user {
						id
					}
				}
			}
		}
	}
`;

type InvoiceViewBillToProps = {
	invoice?: InvoiceViewBillToInvoiceFragment;
	className?: string;
	requestingUserBaseId?: string;
};

type InvoiceViewBillToPropsWithFragment = React.FC<InvoiceViewBillToProps> & {
	fragments: {
		invoiceViewBillToInvoiceFragment: DocumentNode;
	};
};

export const InvoiceViewBillTo: InvoiceViewBillToPropsWithFragment = ( {
	invoice,
	className,
	requestingUserBaseId,
} ) => {
	// get the contact from context when creating a new invoice, otherwise from invoice state
	const contactsContext = useContext( ContactsContext );
	const contact = contactsContext?.contacts?.[ 0 ] || invoice?.contact;
	// customer links
	const name = contact
		? rpcShared.contacts.getCustomerName( contact ).fullName
		: undefined;
	const email = contact ? contact.customer.email : undefined;
	const companyName = contact ? contact.companyName : undefined;

	const viewingUserOrganizationRole = invoice?.contact?.vendor?.users?.find(
		( user ) => user.user?.id === requestingUserBaseId
	)?.role;
	const isAdminOrManagerOfSendingOrganization =
		( viewingUserOrganizationRole &&
			[
				TeamMemberRole.Owner,
				TeamMemberRole.Admin,
				TeamMemberRole.Manager,
			].includes( viewingUserOrganizationRole ) ) ||
		false;

	/* Show a link if there's a contact and either we're making a new invoice or we're an admin or
	manager of the sending organization on an existing invoice */
	const href =
		contact?.customer.id &&
		( !!contactsContext?.contacts?.[ 0 ] || isAdminOrManagerOfSendingOrganization )
			? `/client/${ contact.customer.id }`
			: undefined;

	return (
		<Box className={ classNames( styles.container, className ) }>
			<Typography variant='h6' data-cy='customer-label'>
				Bill to
			</Typography>
			<List disablePadding>
				<ListItem
					disablePadding
					disableGutters
					component={ href ? Link : 'li' }
					href={ href ? href : undefined }
				>
					<ListItemText
						className={ styles.text }
						data-cy='customer-link'
						slotProps={ {
							primary: {
								className: href ? styles.link : undefined,
							},
							secondary: {
								className: styles.secondary,
							},
						} }
						primary={ name }
						secondary={
							<>
								{ email }
								{ companyName ? (
									<>
										<br />
										{ companyName }{ ' ' }
									</>
								) : null }
							</>
						}
					/>
				</ListItem>
			</List>
		</Box>
	);
};

InvoiceViewBillTo.fragments = {
	invoiceViewBillToInvoiceFragment,
};
